import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getNotificationById, acknowledgeNotification } from '../../../service/api';

const NotificationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [notification, setNotification] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNotificationDetails();
    }, [id]);

    const fetchNotificationDetails = async () => {
        try {
            setLoading(true);
            if (!id) return;
            const response = await getNotificationById(id);
            if (response.data.success) {
                setNotification(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching notification details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAcknowledge = async (status: string) => {
        if (!id) return;
        try {
            const response = await acknowledgeNotification(id, { status });
            if (response.data.success) {
                alert(`Notification marked as ${status}`);
                fetchNotificationDetails();
            }
        } catch (error) {
            console.error('Error acknowledging notification:', error);
            alert('Failed to acknowledge notification');
        }
    };

    if (loading) {
        return <div style={{ padding: '24px' }}>Loading...</div>;
    }

    if (!notification) {
        return <div style={{ padding: '24px' }}>Notification not found</div>;
    }

    const priorityColors = {
        Critical: '#dc2626',
        High: '#ea580c',
        Medium: '#f59e0b',
        Low: '#10b981'
    };

    const statusColors = {
        Sent: '#6b7280',
        Delivered: '#3b82f6',
        Read: '#8b5cf6',
        Confirmed: '#10b981',
        Failed: '#dc2626'
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <button
                onClick={() => navigate('/external-emergency-preparedness/notifications')}
                style={{
                    marginBottom: '20px',
                    padding: '8px 16px',
                    backgroundColor: '#e5e7eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                }}
            >
                ← Back to Notifications
            </button>

            {/* Header */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                            {notification.title}
                        </h1>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>
                            ID: {notification.notificationId} | Sent: {new Date(notification.sentAt).toLocaleString()}
                        </p>
                    </div>
                    <span style={{
                        padding: '6px 16px',
                        borderRadius: '12px',
                        backgroundColor: (priorityColors as any)[notification.priority] + '20',
                        color: (priorityColors as any)[notification.priority],
                        fontWeight: 'bold'
                    }}>
                        {notification.priority}
                    </span>
                </div>

                <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '16px' }}>
                    {notification.message}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Channels</p>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {notification.channels.map((channel: any, idx: number) => (
                                <span key={idx} style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: '#e5e7eb',
                                    fontSize: '12px'
                                }}>
                                    {channel}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Target Type</p>
                        <p style={{ fontWeight: '500' }}>{notification.targetType}</p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Delivery Rate</p>
                        <p style={{ fontWeight: '500', color: '#10b981' }}>{notification.deliveryRate}%</p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Confirmation Rate</p>
                        <p style={{ fontWeight: '500', color: '#10b981' }}>{notification.confirmationRate}%</p>
                    </div>
                </div>

                <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                    <button
                        onClick={() => handleAcknowledge('Read')}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Mark as Read
                    </button>
                    <button
                        onClick={() => handleAcknowledge('Confirmed')}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Confirm Receipt
                    </button>
                </div>
            </div>

            {/* Recipients Table */}
            <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
                    Delivery Status ({notification.recipients.length} recipients)
                </h2>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>User</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Channel</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Sent At</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Delivered At</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Read At</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Confirmed At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {notification.recipients.map((recipient: any, idx: number) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                        {recipient.userId?.name || 'Unknown'}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: '#e5e7eb',
                                            fontSize: '12px'
                                        }}>
                                            {recipient.channel}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                        <span style={{
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            backgroundColor: (statusColors as any)[recipient.status] + '20',
                                            color: (statusColors as any)[recipient.status],
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>
                                            {recipient.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                        {recipient.sentAt ? new Date(recipient.sentAt).toLocaleString() : '-'}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                        {recipient.deliveredAt ? new Date(recipient.deliveredAt).toLocaleString() : '-'}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                        {recipient.readAt ? new Date(recipient.readAt).toLocaleString() : '-'}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                                        {recipient.confirmedAt ? new Date(recipient.confirmedAt).toLocaleString() : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default NotificationDetails;
